/* Only request the rendition that fits the visitor's screen. */
if (!customElements.get('outbreak-motion')) {
  class OutbreakMotion extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.video = this.querySelector('video');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = matchMedia('(max-width: 1023px)');
      this.userPaused = false;
      this.autoplayBlocked = false;
      this.playRequest = 0;
      this.inView = false;
      this.video.muted = true;
      this.video.defaultMuted = true;
      this.video.playsInline = true;
      this.video.autoplay = false;
      this.video.addEventListener('playing', () => {
        this.autoplayBlocked = false;
        this.classList.add('is-playing');
        this.updateControls();
      }, options);
      this.video.addEventListener('error', () => this.showPoster(), options);
      this.video.addEventListener('canplay', () => this.syncPlayback(), options);
      document.addEventListener('click', (event) => {
        if (!event.target.closest?.('[data-outbreak-motion-toggle]')) return;
        this.userPaused = this.autoplayBlocked ? false : !this.userPaused;
        this.syncPlayback(true);
      }, options);
      // A direct tap can start motion when Safari requires a user gesture.
      this.addEventListener('click', () => {
        if (this.autoplayBlocked && !this.userPaused) this.syncPlayback(true);
      }, options);
      document.addEventListener('shopify:section:load', () => this.updateControls(), options);
      this.reduced.addEventListener('change', () => this.selectSource(), options);
      this.mobile.addEventListener('change', () => this.selectSource(), options);
      document.addEventListener('visibilitychange', () => this.syncPlayback(), options);
      window.addEventListener('pageshow', () => this.syncPlayback(), options);
      this.observer = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        this.syncPlayback();
      });
      this.observer.observe(this);
      this.selectSource();
    }

    showPoster() {
      this.playRequest++;
      this.video.autoplay = false;
      this.video.pause();
      this.classList.remove('is-playing');
      this.updateControls();
    }

    selectSource() {
      if (this.reduced.matches || navigator.connection?.saveData) {
        this.showPoster();
        this.video.removeAttribute('src');
        this.video.load();
        return;
      }
      const source = this.mobile.matches ? this.dataset.mobile : this.dataset.desktop;
      if (this.video.getAttribute('src') !== source) {
        this.autoplayBlocked = false;
        this.showPoster();
        this.video.src = source;
        this.video.load();
      }
      this.syncPlayback();
    }

    syncPlayback(userGesture = false) {
      if (!this.video.getAttribute('src')) return;
      if (this.reduced.matches || navigator.connection?.saveData || this.userPaused ||
          (!this.inView && !userGesture) || document.hidden) {
        this.playRequest++;
        this.video.autoplay = false;
        this.video.pause();
      } else {
        this.video.autoplay = !this.autoplayBlocked || userGesture;
        if (this.video.paused && (!this.autoplayBlocked || userGesture)) {
          const request = ++this.playRequest;
          this.video.play().catch((error) => {
            // Resizing or pausing may cancel an earlier play request. Its
            // rejection must not stop a newer attempt that is already playing.
            if (request !== this.playRequest || !this.isConnected || error.name === 'AbortError') return;
            this.autoplayBlocked = error.name === 'NotAllowedError';
            this.showPoster();
          });
        }
      }
      this.updateControls();
    }

    updateControls() {
      const available = this.video.getAttribute('src') && !this.reduced.matches &&
        !navigator.connection?.saveData && !this.video.error;
      const label = this.userPaused || this.autoplayBlocked ? 'Play background animation' : 'Pause background animation';
      document.querySelectorAll('[data-outbreak-motion-toggle]').forEach((button) => {
        button.hidden = !available;
        button.querySelector('[data-motion-label]').textContent = label;
        button.setAttribute('aria-label', label);
      });
    }

    disconnectedCallback() {
      this.playRequest++;
      if (this.video) this.video.autoplay = false;
      this.video?.pause();
      this.controller?.abort();
      this.observer?.disconnect();
      document.querySelectorAll('[data-outbreak-motion-toggle]').forEach((button) => { button.hidden = true; });
      this.controller = null;
    }
  }
  customElements.define('outbreak-motion', OutbreakMotion);
}
